import React, { useState } from "react";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CloseOutlined,
} from "@ant-design/icons";

const BookingManager = () => {
  const [bookings, setBookings] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const defaultTimezone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const [formData, setFormData] = useState({
    name: "",
    duration: 30,
    durationMode: "preset", // 'preset' | 'custom'
    timezone: defaultTimezone,
    schedulingWindowMode: "available_now", // 'available_now' | 'date_range'
    schedulingStartDate: "",
    schedulingEndDate: "",
    availability: {
      monday: { enabled: true, start: "09:00", end: "17:00" },
      tuesday: { enabled: true, start: "09:00", end: "17:00" },
      wednesday: { enabled: true, start: "09:00", end: "17:00" },
      thursday: { enabled: true, start: "09:00", end: "17:00" },
      friday: { enabled: true, start: "09:00", end: "17:00" },
      saturday: { enabled: false, start: "10:00", end: "14:00" },
      sunday: { enabled: false, start: "10:00", end: "14:00" },
    },
    schedulingWindow: 30,
    bufferTime: 0,
    maxBookingsPerDay: 10,
    adjustedAvailability: [],
    color: "#3b82f6",
  });

  const days = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];
  const dayLabels = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  const handleAddNew = () => {
    setEditingId(null);
    setFormData({
      name: "",
      duration: 30,
      durationMode: "preset",
      timezone: defaultTimezone,
      schedulingWindowMode: "available_now",
      schedulingStartDate: "",
      schedulingEndDate: "",
      availability: {
        monday: { enabled: true, start: "09:00", end: "17:00" },
        tuesday: { enabled: true, start: "09:00", end: "17:00" },
        wednesday: { enabled: true, start: "09:00", end: "17:00" },
        thursday: { enabled: true, start: "09:00", end: "17:00" },
        friday: { enabled: true, start: "09:00", end: "17:00" },
        saturday: { enabled: false, start: "10:00", end: "14:00" },
        sunday: { enabled: false, start: "10:00", end: "14:00" },
      },
      schedulingWindow: 30,
      bufferTime: 0,
      maxBookingsPerDay: 10,
      adjustedAvailability: [],
      color: "#3b82f6",
    });
    setIsModalOpen(true);
  };

  const handleEdit = (id) => {
    setEditingId(id);
    setFormData(bookings[id]);
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    setBookings(bookings.filter((_, i) => i !== id));
  };

  const handleSave = () => {
    if (!formData.name.trim()) {
      alert("Please enter a booking name");
      return;
    }

    if (editingId !== null) {
      const updated = [...bookings];
      updated[editingId] = formData;
      setBookings(updated);
    } else {
      setBookings([...bookings, formData]);
    }

    setIsModalOpen(false);
  };

  const handleAvailabilityChange = (day, field, value) => {
    setFormData({
      ...formData,
      availability: {
        ...formData.availability,
        [day]: {
          ...formData.availability[day],
          [field]: value,
        },
      },
    });
  };

  const handleAddAdjustedAvailability = () => {
    setFormData({
      ...formData,
      adjustedAvailability: [
        ...formData.adjustedAvailability,
        { date: "", start: "09:00", end: "17:00", enabled: true },
      ],
    });
  };

  const handleRemoveAdjustedAvailability = (index) => {
    setFormData({
      ...formData,
      adjustedAvailability: formData.adjustedAvailability.filter(
        (_, i) => i !== index
      ),
    });
  };

  const handleAdjustedAvailabilityChange = (index, field, value) => {
    const updated = [...formData.adjustedAvailability];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, adjustedAvailability: updated });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
                Booking Calendar
              </h1>
              <p className="mt-2 text-slate-600">
                Manage your appointment types and availability with a polished
                experience.
              </p>
            </div>
            {/* Create Button */}
            <button
              onClick={handleAddNew}
              className="inline-flex items-center gap-2 self-start sm:self-auto bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white px-5 py-2.5 rounded-xl font-medium shadow-sm hover:shadow transition-all"
            >
              <PlusOutlined style={{ fontSize: "18px" }} />
              New schedule
            </button>
          </div>
        </div>

        {/* Bookings List */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {bookings.length === 0 ? (
            <div className="lg:col-span-2 xl:col-span-3">
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center shadow-sm">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                  <PlusOutlined />
                </div>
                <p className="text-slate-700 font-medium">
                  No booking schedules yet
                </p>
                <p className="mt-1 text-sm text-slate-500">
                  Create your first schedule to start accepting bookings.
                </p>
                <button
                  onClick={handleAddNew}
                  className="mt-6 inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  <PlusOutlined /> Create schedule
                </button>
              </div>
            </div>
          ) : (
            bookings.map((booking, idx) => (
              <div
                key={idx}
                className="group bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-4 h-4 rounded ring-2 ring-offset-2 ring-slate-100"
                        style={{ backgroundColor: booking.color }}
                      />
                      <h3 className="text-lg font-semibold text-slate-900 tracking-tight">
                        {booking.name}
                      </h3>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(idx)}
                        className="p-2 hover:bg-slate-100 rounded-lg text-slate-600 transition-colors border border-transparent hover:border-slate-200"
                      >
                        <EditOutlined style={{ fontSize: "18px" }} />
                      </button>
                      <button
                        onClick={() => handleDelete(idx)}
                        className="p-2 hover:bg-red-50 rounded-lg text-red-600 transition-colors border border-transparent hover:border-red-200"
                      >
                        <DeleteOutlined style={{ fontSize: "18px" }} />
                      </button>
                    </div>
                  </div>

                  <div className="mt-2 grid grid-cols-2 gap-3 text-sm">
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <span className="text-slate-600">Duration</span>
                      <div className="font-semibold text-slate-900">
                        {booking.duration} min
                      </div>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <span className="text-slate-600">Buffer</span>
                      <div className="font-semibold text-slate-900">
                        {booking.bufferTime} min
                      </div>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <span className="text-slate-600">Max/day</span>
                      <div className="font-semibold text-slate-900">
                        {booking.maxBookingsPerDay}
                      </div>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <span className="text-slate-600">Window</span>
                      <div className="font-semibold text-slate-900">
                        {booking.schedulingWindow} days
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t">
                    <p className="text-xs font-semibold text-slate-600 mb-3 tracking-wider">
                      AVAILABILITY
                    </p>
                    <div className="space-y-2">
                      {days.map(
                        (day, i) =>
                          booking.availability[day].enabled && (
                            <div key={day} className="text-xs text-slate-600">
                              <span className="font-medium">
                                {dayLabels[i]}:
                              </span>{" "}
                              {booking.availability[day].start} -{" "}
                              {booking.availability[day].end}
                            </div>
                          )
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl ring-1 ring-slate-900/5 max-w-2xl w-full my-8">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-2xl font-bold text-slate-900">
                {editingId !== null
                  ? "Edit Booking"
                  : "Create Booking Schedule"}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <CloseOutlined style={{ fontSize: "20px" }} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-96">
              {/* Booking Name */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-slate-900 mb-2">
                  Booking Appointment Schedule
                </label>
                <input
                  type="text"
                  placeholder="e.g., 30-minute meeting"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Appointment duration */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-slate-900 mb-2">
                  Appointment duration
                </label>
                <p className="text-xs text-slate-600 mb-3">
                  Choose a preset or set a custom duration.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex items-center gap-2">
                    <select
                      value={formData.durationMode}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          durationMode: e.target.value,
                        })
                      }
                      className="flex-1 px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    >
                      <option value="preset">Preset</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                  {formData.durationMode === "preset" ? (
                    <select
                      value={String(formData.duration)}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          duration: parseInt(e.target.value),
                        })
                      }
                      className="px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    >
                      {[15, 30, 45, 60].map((dur) => (
                        <option key={dur} value={dur}>
                          {dur} minutes
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="1"
                        max="480"
                        value={formData.duration}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            duration: parseInt(e.target.value) || 30,
                          })
                        }
                        className="px-3 py-2 border border-slate-300 rounded w-28 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-600">minutes</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Timezone selection */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-slate-900 mb-2">
                  Timezone
                </label>
                <p className="text-xs text-slate-600 mb-3">
                  Times below are interpreted in this timezone.
                </p>
                <select
                  value={formData.timezone}
                  onChange={(e) =>
                    setFormData({ ...formData, timezone: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  {[
                    formData.timezone,
                    "UTC",
                    "America/New_York",
                    "Europe/London",
                    "Asia/Kolkata",
                    "Asia/Dubai",
                    "Australia/Sydney",
                  ]
                    .filter((tz, idx, arr) => arr.indexOf(tz) === idx)
                    .map((tz) => (
                      <option key={tz} value={tz}>
                        {tz}
                      </option>
                    ))}
                </select>
              </div>

              {/* Color Picker */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-slate-900 mb-2">
                  Calendar Color
                </label>
                <div className="flex gap-3 flex-wrap">
                  {[
                    "#3b82f6",
                    "#ef4444",
                    "#10b981",
                    "#f59e0b",
                    "#8b5cf6",
                    "#ec4899",
                  ].map((color) => (
                    <button
                      key={color}
                      onClick={() => setFormData({ ...formData, color })}
                      className={`w-8 h-8 rounded ${
                        formData.color === color
                          ? "ring-2 ring-offset-2 ring-slate-400"
                          : ""
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>

              {/* General Availability */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-slate-900 mb-3">
                  General Availability
                </label>
                <p className="text-xs text-slate-600 mb-4">
                  Set when you're regularly available for appointments
                </p>
                <div className="space-y-3">
                  {days.map((day, i) => (
                    <div key={day} className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={formData.availability[day].enabled}
                        onChange={(e) =>
                          handleAvailabilityChange(
                            day,
                            "enabled",
                            e.target.checked
                          )
                        }
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-slate-700 w-24">
                        {dayLabels[i]}
                      </span>
                      {formData.availability[day].enabled && (
                        <div className="flex gap-2 flex-1">
                          <input
                            type="time"
                            value={formData.availability[day].start}
                            onChange={(e) =>
                              handleAvailabilityChange(
                                day,
                                "start",
                                e.target.value
                              )
                            }
                            className="px-2 py-1 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <span className="text-slate-500">-</span>
                          <input
                            type="time"
                            value={formData.availability[day].end}
                            onChange={(e) =>
                              handleAvailabilityChange(
                                day,
                                "end",
                                e.target.value
                              )
                            }
                            className="px-2 py-1 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Scheduling window */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-slate-900 mb-2">
                  Scheduling window
                </label>
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="radio"
                        name="schedulingWindowMode"
                        checked={
                          formData.schedulingWindowMode === "available_now"
                        }
                        onChange={() =>
                          setFormData({
                            ...formData,
                            schedulingWindowMode: "available_now",
                          })
                        }
                        className="h-4 w-4 border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span>Available now</span>
                    </label>
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="radio"
                        name="schedulingWindowMode"
                        checked={formData.schedulingWindowMode === "date_range"}
                        onChange={() =>
                          setFormData({
                            ...formData,
                            schedulingWindowMode: "date_range",
                          })
                        }
                        className="h-4 w-4 border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span>Start and end dates</span>
                    </label>
                  </div>
                  {formData.schedulingWindowMode === "available_now" ? (
                    <div className="flex gap-2 items-center">
                      <input
                        type="number"
                        min="1"
                        max="365"
                        value={formData.schedulingWindow}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            schedulingWindow: parseInt(e.target.value) || 30,
                          })
                        }
                        className="px-3 py-2 border border-slate-300 rounded w-24 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-600">
                        days in advance
                      </span>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-slate-600 mb-1">
                          Start date
                        </label>
                        <input
                          type="date"
                          value={formData.schedulingStartDate}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              schedulingStartDate: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-600 mb-1">
                          End date
                        </label>
                        <input
                          type="date"
                          value={formData.schedulingEndDate}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              schedulingEndDate: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Adjusted Availability */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-semibold text-slate-900">
                    Adjusted Availability
                  </label>
                  <button
                    onClick={handleAddAdjustedAvailability}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                  >
                    + Add Exception
                  </button>
                </div>
                <p className="text-xs text-slate-600 mb-3">
                  Indicate times on specific dates when you're available
                </p>
                <div className="space-y-3">
                  {formData.adjustedAvailability.map((adj, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <input
                        type="date"
                        value={adj.date}
                        onChange={(e) =>
                          handleAdjustedAvailabilityChange(
                            idx,
                            "date",
                            e.target.value
                          )
                        }
                        className="px-2 py-1 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        type="time"
                        value={adj.start}
                        onChange={(e) =>
                          handleAdjustedAvailabilityChange(
                            idx,
                            "start",
                            e.target.value
                          )
                        }
                        className="px-2 py-1 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-slate-500">-</span>
                      <input
                        type="time"
                        value={adj.end}
                        onChange={(e) =>
                          handleAdjustedAvailabilityChange(
                            idx,
                            "end",
                            e.target.value
                          )
                        }
                        className="px-2 py-1 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        onClick={() => handleRemoveAdjustedAvailability(idx)}
                        className="p-1.5 hover:bg-red-50 rounded-lg text-red-600 transition-colors border border-transparent hover:border-red-200"
                      >
                        <DeleteOutlined style={{ fontSize: "16px" }} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Booking appointment settings */}
              <div className="mb-2">
                <p className="text-xs font-semibold text-slate-600 mb-3 tracking-wider">
                  BOOKING APPOINTMENT SETTINGS
                </p>
                {/* Buffer Time */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-slate-900 mb-2">
                    Buffer time
                  </label>
                  <p className="text-xs text-slate-600 mb-3">
                    Add time between appointment slots
                  </p>
                  <div className="flex gap-2 items-center">
                    <input
                      type="number"
                      min="0"
                      max="120"
                      step="5"
                      value={formData.bufferTime}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          bufferTime: parseInt(e.target.value) || 0,
                        })
                      }
                      className="px-3 py-2 border border-slate-300 rounded w-24 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm text-slate-600">minutes</span>
                  </div>
                </div>

                {/* Maximum bookings per day */}
                <div className="mb-2">
                  <label className="block text-sm font-semibold text-slate-900 mb-2">
                    Maximum bookings per day
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={formData.maxBookingsPerDay}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        maxBookingsPerDay: parseInt(e.target.value) || 10,
                      })
                    }
                    className="w-full px-3 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex gap-3 p-6 border-t bg-slate-50">
              <button
                onClick={() => setIsModalOpen(false)}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 font-medium hover:bg-slate-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium shadow-sm hover:shadow transition-all flex items-center justify-center gap-2"
              >
                {/* <Save size={18} /> */}
                {editingId !== null ? "Update" : "Create"} Schedule
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingManager;
